// src/components/UserProfileModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import styles from './UserProfileModal.module.css';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
// Import the new components/utils
import PasswordStrengthIndicator from './Auth/PasswordStrengthIndicator';
import { getPasswordValidationState } from '../utils/validation';

// (Keep Modal.setAppElement and constants)
Modal.setAppElement('#root');
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE_MB = 5;

const UserProfileModal = () => {
    // (Keep existing state hooks: currentUser, resetPassword, userProfile, modal states, etc.)
    const { currentUser, resetPassword } = useAuth();
    const {
        userProfile,
        isUserProfileModalOpen,
        setIsUserProfileModalOpen,
        isUserProfileModalLoading,
        setIsUserProfileModalLoading,
        userProfileModalError,
        setUserProfileModalError,
        updateUserProfile: updateUserProfileDataContext,
    } = useData();

    // (Keep state for editable fields: userName)
    const [userName, setUserName] = useState('');

    // (Keep state for photo upload/management)
    const [photoInputMode, setPhotoInputMode] = useState('current');
    const [currentPhotoUrlInput, setCurrentPhotoUrlInput] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState('');
    const [photoPreviewSrc, setPhotoPreviewSrc] = useState('');
    const [photoError, setPhotoError] = useState(''); // Changed to string for better messages
    const [clearExistingPhoto, setClearExistingPhoto] = useState(false);
    const [imageLoadKey, setImageLoadKey] = useState(Date.now());

    // (Keep state for password change)
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordChangeError, setPasswordChangeError] = useState('');
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    // NEW: State for new password validation
    const [newPasswordValidation, setNewPasswordValidation] = useState(null);
    const [showNewPasswordRequirements, setShowNewPasswordRequirements] = useState(false);

    const fileInputRef = useRef(null);

    // (Keep safe setter functions)
    const safeSetIsUserProfileModalOpen = setIsUserProfileModalOpen || (() => console.warn("setIsUserProfileModalOpen not available"));
    const safeSetIsUserProfileModalLoading = setIsUserProfileModalLoading || (() => console.warn("setIsUserProfileModalLoading not available"));
    const safeSetUserProfileModalError = setUserProfileModalError || (() => console.warn("setUserProfileModalError not available"));
    const safeUpdateUserProfileDataContext = updateUserProfileDataContext || (() => { console.warn("updateUserProfileDataContext not available"); return Promise.resolve({ status: 'error', message: 'Context function not available' }); });

    // --- Effects ---

    // (Keep Effect 1: Populate form on open/reset on close - no changes needed here)
    useEffect(() => {
        // ... (existing logic to populate userName, photo state, reset password fields) ...
        if (isUserProfileModalOpen && userProfile) {
            setUserName(userProfile.userName || '');
            // Reset photo state
             let initialPreviewSrc = '';
            if (userProfile.photoURL) {
                 initialPreviewSrc = userProfile.photoURL;
                 setCurrentPhotoUrlInput(userProfile.photoURL);
                 setPhotoInputMode('current');
            } else {
                 setPhotoInputMode('url');
            }
            setPhotoPreviewSrc(initialPreviewSrc);
            setImageLoadKey(Date.now());
            setPhotoError('');
            setSelectedFile(null);
            if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
                 URL.revokeObjectURL(filePreviewUrl);
            }
            setFilePreviewUrl('');
            setClearExistingPhoto(false);

            // Reset password fields and status
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setPasswordChangeError('');
            setPasswordChangeSuccess('');
            setNewPasswordValidation(null); // Reset password validation state
            setShowNewPasswordRequirements(false); // Hide requirements initially

        } else if (!isUserProfileModalOpen) {
             // Reset form state when modal is closed
             setUserName('');
             setPhotoInputMode('url');
             setCurrentPhotoUrlInput('');
             setPhotoPreviewSrc('');
             setPhotoError('');
             setSelectedFile(null);
              if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
                 URL.revokeObjectURL(filePreviewUrl);
             }
             setFilePreviewUrl('');
             setClearExistingPhoto(false);
             setImageLoadKey(Date.now());
             setCurrentPassword('');
             setNewPassword('');
             setConfirmNewPassword('');
             setPasswordChangeError('');
             setPasswordChangeSuccess('');
             setNewPasswordValidation(null); // Reset password validation state
             setShowNewPasswordRequirements(false); // Hide requirements
             // setUserProfileModalError(null); // Clear context error if needed
        }
    }, [isUserProfileModalOpen, userProfile]); // Added filePreviewUrl dependency

    // (Keep Effect 2: Update photo preview on interaction - no changes needed here)
     useEffect(() => {
         if (!isUserProfileModalOpen) return;
        let interactionPreviewSrc = '';
        if (clearExistingPhoto) {
            interactionPreviewSrc = '';
        } else if (photoInputMode === 'file' && filePreviewUrl) {
            interactionPreviewSrc = filePreviewUrl;
        } else if (photoInputMode === 'url') {
            interactionPreviewSrc = currentPhotoUrlInput;
        } else if (photoInputMode === 'current' && userProfile?.photoURL) {
             interactionPreviewSrc = userProfile.photoURL;
        }
        setPhotoPreviewSrc(interactionPreviewSrc);
        // Don't reset photoError here, let handlePhotoPreviewError manage it
        setImageLoadKey(Date.now());
    }, [isUserProfileModalOpen, photoInputMode, filePreviewUrl, selectedFile, currentPhotoUrlInput, clearExistingPhoto, userProfile?.photoURL]);

    // (Keep Effect 3: Cleanup object URLs - no changes needed here)
     useEffect(() => {
        const currentBlobUrl = filePreviewUrl;
        return () => {
            if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentBlobUrl);
                // console.log("[UserProfileModal] Revoked object URL:", currentBlobUrl);
            }
        };
    }, [filePreviewUrl]);

    // NEW Effect: Validate new password as it changes
    useEffect(() => {
        if (!isUserProfileModalOpen) return; // Only run when modal is open

        if (newPassword) {
            const validation = getPasswordValidationState(newPassword);
            setNewPasswordValidation(validation);
            setShowNewPasswordRequirements(true); // Keep requirements visible while typing
        } else {
            setNewPasswordValidation(null); // Clear validation if empty
            // Optionally hide requirements if empty, or keep visible if focused (handled by focus/blur)
            // setShowNewPasswordRequirements(false);
        }
    }, [newPassword, isUserProfileModalOpen]);


    // --- Handlers ---

    // (Keep handleCloseRequest - no changes needed here)
    const handleCloseRequest = useCallback(() => {
        if (isUserProfileModalLoading || isChangingPassword) return;
        if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
             URL.revokeObjectURL(filePreviewUrl);
        }
        setFilePreviewUrl('');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setClearExistingPhoto(false);
        setPhotoError('');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setPasswordChangeError('');
        setPasswordChangeSuccess('');
        setNewPasswordValidation(null); // Reset validation state on close
        setShowNewPasswordRequirements(false);
        setUserProfileModalError(null);
        setIsUserProfileModalOpen(false);
    }, [isUserProfileModalLoading, isChangingPassword, filePreviewUrl, setIsUserProfileModalOpen, setUserProfileModalError]);

    // (Keep handlePhotoModeChange - no changes needed here)
     const handlePhotoModeChange = (event) => {
        const newMode = event.target.value;
        setPhotoInputMode(newMode);
        setClearExistingPhoto(false);
        setPhotoError(''); // Clear photo error on mode change
    };

    // (Keep handleFileChange - update error message)
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
         if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(filePreviewUrl);
              setFilePreviewUrl('');
         }
        if (!file) {
            setSelectedFile(null);
            setClearExistingPhoto(false);
            setPhotoError(''); // Clear error if no file selected
            return;
        }
         if (!ALLOWED_MIME_TYPES.includes(file.type)) {
             setPhotoError(`Invalid file type. Allowed: ${ALLOWED_MIME_TYPES.join(', ')}`);
             setSelectedFile(null);
             if (fileInputRef.current) fileInputRef.current.value = '';
             setClearExistingPhoto(false);
             return;
         }
         if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
             setPhotoError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
             setSelectedFile(null);
             if (fileInputRef.current) fileInputRef.current.value = '';
             setClearExistingPhoto(false);
             return;
         }
        setSelectedFile(file);
        setPhotoError(''); // Clear any previous photo errors
        setClearExistingPhoto(false);
        const newPreviewUrl = URL.createObjectURL(file);
        setFilePreviewUrl(newPreviewUrl);
    };

    // (Keep handleClearPhoto - update error message)
     const handleClearPhoto = () => {
         if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(filePreviewUrl);
         }
         setClearExistingPhoto(true);
         setSelectedFile(null);
         setFilePreviewUrl('');
         setCurrentPhotoUrlInput('');
         if (fileInputRef.current) fileInputRef.current.value = '';
         setPhotoInputMode('url');
         setPhotoError(''); // Clear photo error on clear
     };

    // (Keep handlePhotoPreviewError - update error message)
     const handlePhotoPreviewError = () => {
         if (photoPreviewSrc) {
             console.warn("[UserProfileModal] Failed to load photo image preview from:", photoPreviewSrc);
             setPhotoError("Could not load image preview."); // Set specific error message
         } else {
             setPhotoError(''); // Clear error if src is empty
         }
     };

    // (Keep handleSaveProfile - update error message)
    const handleSaveProfile = async () => {
        if (isUserProfileModalLoading || isChangingPassword) return;
        setUserProfileModalError(null);
        setPhotoError(''); // Clear previous photo errors
        setIsUserProfileModalLoading(true);
         if (!userName.trim()) {
             setUserProfileModalError("Username cannot be empty.");
             setIsUserProfileModalLoading(false);
             return;
         }
         // (Keep payload logic)
         let payload;
         const commonData = { userName: userName.trim() };
         if (photoInputMode === 'file' && selectedFile) {
             payload = new FormData();
             payload.append('userName', commonData.userName);
             payload.append('photoFile', selectedFile);
         } else if (clearExistingPhoto) {
              payload = new FormData();
              payload.append('userName', commonData.userName);
              payload.append('clearPhoto', 'true');
         } else {
             payload = { ...commonData };
             if (photoInputMode === 'url') {
                  payload.photoURL = currentPhotoUrlInput.trim() || null;
             }
         }

        const result = await updateUserProfileDataContext(currentUser.uid, payload);
        if (result?.status === 'success') {
             setShowSuccessMessage(true);
             setTimeout(() => setShowSuccessMessage(false), 3000);
             if (photoInputMode === 'file' && selectedFile) {
                 setSelectedFile(null);
                 if (fileInputRef.current) fileInputRef.current.value = '';
                 if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
                     URL.revokeObjectURL(filePreviewUrl);
                     setFilePreviewUrl('');
                 }
             }
              setClearExistingPhoto(false);
        } else {
             setUserProfileModalError(result?.message || "Failed to save profile.");
        }
        setIsUserProfileModalLoading(false);
    };

    // NEW: Handlers for new password focus/blur to control indicator visibility
    const handleNewPasswordFocus = () => {
        setShowNewPasswordRequirements(true); // Show requirements when field is focused
    }
    const handleNewPasswordBlur = () => {
        // Hide requirements on blur ONLY if the field is empty
        if (!newPassword) {
            setShowNewPasswordRequirements(false);
        }
    }


    // UPDATE: handleChangePassword to include new validation
    const handleChangePassword = async () => {
        if (isUserProfileModalLoading || isChangingPassword) return;

        setPasswordChangeError('');
        setPasswordChangeSuccess('');

        // --- Frontend Validations ---
        if (!currentUser || !currentUser.email) {
            setPasswordChangeError("User not authenticated.");
            return;
        }
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setPasswordChangeError("All password fields are required.");
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setPasswordChangeError("New passwords do not match.");
            return;
        }
        if (newPassword === currentPassword) {
             setPasswordChangeError("New password cannot be the same as the current password.");
             return;
        }

        // NEW: Check password strength using the utility function
        const validation = getPasswordValidationState(newPassword);
        setNewPasswordValidation(validation); // Update state for indicator
        setShowNewPasswordRequirements(true); // Ensure indicator is visible

        if (!validation.isValid) {
            setPasswordChangeError("New password does not meet all requirements. Please check the list below the input field.");
            return; // Stop if requirements not met
        }
        // --- End Frontend Validations ---


        setIsChangingPassword(true); // Start loading state *after* frontend checks pass

        try {
            const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            console.log("[UserProfileModal] User re-authenticated successfully.");

            await updatePassword(currentUser, newPassword);

            console.log("[UserProfileModal] Password updated successfully.");
            setPasswordChangeSuccess("Password updated successfully!");
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setNewPasswordValidation(null); // Clear validation state on success
            setShowNewPasswordRequirements(false); // Hide indicator on success

        } catch (error) {
            console.error("[UserProfileModal] Password change error:", error);
            let errorMessage = "Failed to change password.";
            switch (error.code) {
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                    errorMessage = 'Incorrect current password.';
                    break;
                case 'auth/requires-recent-login':
                    errorMessage = 'For security, please log out and log back in before changing your password.';
                    break;
                case 'auth/weak-password': // Should be caught by our check, but keep as fallback
                    errorMessage = 'Password is too weak (rejected by server).';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'User not found in authentication system.';
                    break;
                default:
                     errorMessage = `Failed to change password: ${error.message}`;
                    break;
            }
            setPasswordChangeError(errorMessage);
        } finally {
            setIsChangingPassword(false);
        }
    };

    // (Keep handleForgotPasswordClick - no changes needed here)
    const handleForgotPasswordClick = async () => {
         if (isUserProfileModalLoading || isChangingPassword) return;
         if (!currentUser?.email) {
             setPasswordChangeError("Could not send reset email. User email not available.");
             return;
         }
         setPasswordChangeError('');
         setPasswordChangeSuccess('');
         // Optional: setIsChangingPassword(true); // Show loading
         try {
             await resetPassword(currentUser.email);
             setPasswordChangeSuccess(`A password reset email has been sent to ${currentUser.email}. Please check your inbox.`);
         } catch (error) {
             console.error("[UserProfileModal] Error sending password reset email:", error);
             let errorMessage = "Failed to send password reset email.";
             switch (error.code) {
                 case 'auth/user-not-found': errorMessage = 'No user found with this email address.'; break;
                 case 'auth/invalid-email': errorMessage = 'The email address is invalid.'; break;
                 default: errorMessage = `Failed to send reset email: ${error.message}`;
             }
             setPasswordChangeError(errorMessage);
         } finally {
             // Optional: setIsChangingPassword(false); // Reset loading
         }
     };

    // (Keep showSuccessMessage state)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    // (Keep Early Exit Condition)
    if (!isUserProfileModalOpen || !userProfile) {
        if (isUserProfileModalOpen && !userProfile) {
             return ( /* ... loading modal ... */
                  <Modal isOpen={true} className={`${styles.modalContent} ${styles.loading}`} overlayClassName={styles.modalOverlay} contentLabel="Loading User Profile">
                      <div className={styles.spinnerOverlay}>
                           <div className={styles.spinner}></div><p>Loading User Profile Data...</p>
                      </div>
                  </Modal>
             );
        }
        return null;
     }

    const isContentLoading = isUserProfileModalLoading || isChangingPassword;

    return (
        <Modal
            isOpen={isUserProfileModalOpen}
            onRequestClose={handleCloseRequest}
            className={`${styles.modalContent} ${isContentLoading ? styles.loading : ''}`}
            overlayClassName={styles.modalOverlay}
            contentLabel="Manage User Profile"
            shouldCloseOnOverlayClick={!isContentLoading}
            shouldCloseOnEsc={!isContentLoading}
            aria={{ modal: 'true', labelledby: 'userProfileModalTitle' }}
        >
             {isContentLoading && <div className={styles.spinnerOverlay}>
                 <div className={styles.spinner}></div>
                 <p>{isUserProfileModalLoading ? 'Saving Profile...' : 'Changing Password...'}</p>
             </div>}

            <h2 id="userProfileModalTitle">Manage User Profile</h2>

            {/* Global Modal Error */}
            {userProfileModalError && (
                <div className={styles.modalError}>Error: {userProfileModalError}</div>
            )}
             {/* Local Photo Validation Error */}
             {photoError && !userProfileModalError && (
                 <div className={styles.modalError} style={{ backgroundColor: '#6c500f', borderColor: '#a07817', color: '#f3e5ab' }}>
                    Photo Issue: {photoError}
                 </div>
             )}


            <div className={styles.modalBody}>
                {/* --- Left Panel --- */}
                <div className={styles.leftPanel}>
                    {/* Read-only Fields */}
                    <div className={styles.readOnlyGroup}>
                         <p><strong>Email:</strong> {userProfile.email}</p>
                         <p><strong>Role:</strong> {userProfile.role}</p>
                         <p><strong>Job Description:</strong> {userProfile.jobDescription}</p>
                         <p><strong>Sign Up Date:</strong> {userProfile.signUpDate ? formatSignUpDate(userProfile.signUpDate) : "N/A"}</p>
                     </div>
                    {/* Username */}
                    <div className={styles.formGroup}>
                        <label htmlFor="userNameInput">Username:</label>
                        <input type="text" id="userNameInput" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Enter username" disabled={isContentLoading} required aria-required="true" />
                    </div>
                    {/* Photo Input Section */}
                    <fieldset className={styles.photoFieldset}>
                        <legend>Profile Photo</legend>
                        <div className={styles.radioGroup}>
                            {userProfile?.photoURL && (<label><input type="radio" name="photoMode" value="current" checked={photoInputMode === 'current'} onChange={handlePhotoModeChange} disabled={isContentLoading} /> Current Photo</label>)}
                            <label><input type="radio" name="photoMode" value="url" checked={photoInputMode === 'url'} onChange={handlePhotoModeChange} disabled={isContentLoading} /> Enter URL</label>
                            <label><input type="radio" name="photoMode" value="file" checked={photoInputMode === 'file'} onChange={handlePhotoModeChange} disabled={isContentLoading} /> Upload File</label>
                        </div>
                        {photoInputMode === 'url' && (
                            <div className={styles.formGroup} style={{ marginTop: '10px' }}>
                                <label htmlFor="userPhotoUrlInput">Photo URL:</label>
                                <input type="text" id="userPhotoUrlInput" value={currentPhotoUrlInput} onChange={(e) => setCurrentPhotoUrlInput(e.target.value)} placeholder="https://example.com/photo.jpg" disabled={isContentLoading} />
                            </div>
                        )}
                        {photoInputMode === 'file' && (
                            <div className={styles.formGroup} style={{ marginTop: '10px' }}>
                                <label htmlFor="userPhotoFileInput">Select Photo File:</label>
                                <input type="file" id="userPhotoFileInput" ref={fileInputRef} onChange={handleFileChange} accept={ALLOWED_MIME_TYPES.join(',')} disabled={isContentLoading} className={styles.fileInput} />
                                <span className={styles.fileInputHint}>Max {MAX_FILE_SIZE_MB}MB. Allowed: {(ALLOWED_MIME_TYPES || []).map(t => t.split('/')[1]).join(', ')}</span>
                            </div>
                        )}
                         <button type="button" onClick={handleClearPhoto} className={styles.clearButton} disabled={isContentLoading || (!selectedFile && !currentPhotoUrlInput && !userProfile?.photoURL && photoInputMode !== 'current')} title="Remove profile photo"> Clear Photo </button>
                    </fieldset>
                     {/* Photo Preview Area */}
                    <fieldset className={styles.photoPreviewArea}>
                        <legend>Photo Preview</legend>
                        {photoPreviewSrc && !photoError ? (
                            <img key={imageLoadKey} src={photoPreviewSrc} alt="User profile photo preview" className={styles.photoPreviewImage} onError={handlePhotoPreviewError} />
                        ) : (
                            <div className={styles.photoPlaceholder}>
                                {photoError ? photoError : "No photo provided or selected"}
                            </div>
                        )}
                    </fieldset>
                </div> {/* End Left Panel */}

                {/* --- Right Panel: Password Change --- */}
                <div className={styles.rightPanel}>
                     <h3>Change Password</h3>
                     {/* Display password change errors */}
                     {passwordChangeError && (
                         <div className={styles.passwordError}>{passwordChangeError}</div>
                     )}
                     {/* Display password change success */}
                     {passwordChangeSuccess && (
                         <div className={styles.passwordSuccess}>{passwordChangeSuccess}</div>
                     )}

                     {/* Current Password */}
                     <div className={styles.formGroup}>
                        <label htmlFor="currentPasswordInput">Current Password:</label>
                        <input type="password" id="currentPasswordInput" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={isContentLoading} autoComplete="current-password" />
                    </div>

                     {/* New Password */}
                     <div className={styles.formGroup}>
                        <label htmlFor="newPasswordInput">New Password:</label>
                        <input
                            type="password"
                            id="newPasswordInput"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            onFocus={handleNewPasswordFocus} // Show reqs on focus
                            onBlur={handleNewPasswordBlur}   // Hide if empty on blur
                            disabled={isContentLoading}
                            autoComplete="new-password"
                            aria-describedby={showNewPasswordRequirements ? "new-password-reqs" : undefined}
                         />
                         {/* Password Strength Indicator for New Password */}
                         {showNewPasswordRequirements && (
                            <div id="new-password-reqs">
                                <PasswordStrengthIndicator validationState={newPasswordValidation} />
                            </div>
                         )}
                    </div>

                    {/* Confirm New Password */}
                    <div className={styles.formGroup}>
                        <label htmlFor="confirmNewPasswordInput">Confirm New Password:</label>
                        <input type="password" id="confirmNewPasswordInput" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} disabled={isContentLoading} autoComplete="new-password" />
                    </div>

                    {/* Change Password Button */}
                    <button
                        onClick={handleChangePassword}
                        className={`${styles.actionButton} ${styles.passwordButton}`}
                        disabled={isContentLoading || !currentPassword || !newPassword || !confirmNewPassword}
                    >
                        {isChangingPassword ? 'Changing...' : 'Change Password'}
                    </button>

                    {/* Forgot Password Link */}
                     <div className={styles.forgotPasswordLink}>
                         <button type="button" onClick={handleForgotPasswordClick} disabled={isContentLoading}> Forgot your password? </button>
                     </div>

                </div> {/* End Right Panel */}
            </div> {/* End Modal Body */}

             {/* Success message area */}
            {showSuccessMessage && (
            <div className={styles.modalSuccess}>
                ✓ Profile data saved successfully!
            </div>
            )}

            {/* Bottom: Action Buttons */}
            <div className={styles.modalActions}>
                <button onClick={handleSaveProfile} className={`${styles.actionButton} ${styles.saveButton}`} disabled={isContentLoading || !userName.trim()}>
                    {isUserProfileModalLoading ? 'Saving Profile...' : 'Save Changes'}
                </button>
                <button onClick={handleCloseRequest} className={`${styles.actionButton} ${styles.closeButton}`} disabled={isContentLoading}>
                    Close
                </button>
            </div>
        </Modal>
    );
};

// (Keep formatSignUpDate helper function)
const formatSignUpDate = (signUpDateStr) => {
  try {
    if (!signUpDateStr) return "N/A";
    const parsedDate = new Date(signUpDateStr);
    if (isNaN(parsedDate.getTime())) {
      return signUpDateStr; // Return original if invalid
    }
    return parsedDate.toLocaleString('en-US', { /* ... formatting options ... */
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
     });
  } catch (error) {
    console.error("[UserProfileModal] Error formatting SignUpDate:", error);
    return "N/A";
  }
};

export default UserProfileModal;